import { StoreActiveCategories, Article, Item, SelectedArticles, StoreAmounts, StorePickingData } from '../../types/Picklist'
import { ONE_DAY_IN_MS, ONE_WEEK_IN_MS } from '../../utils/dateUtils'
import { sortByNumber, SortDirection } from '../../utils/sortUtils'
import { stockText } from '../../utils/stockUtils'

export const SOLD_SINCE_LAST_RESTOCK = 'sold-since-last-restock'
export const STOCK_VS_RESTOCK = 'stock-vs-restock'
export const STOCK_STATUS_OUT = 'Slut'
export const STOCK_STATUS_RUNNING_OUT = 'Snart slut'
export const STOCK_STATUS_ALL = 'Alla'
export const MISSING_STORE_NAME = 'Butiksnamn saknas'

export interface PickDisplayOption {
  id: string
  value: string
  selected: boolean
}

export interface SortingChangedEvent extends CustomEvent {
  detail: PickDisplayOption[]
}

export const PICK_DISPLAY_DEFAULT_OPTIONS: PickDisplayOption[] = [
  { id: STOCK_VS_RESTOCK, value: 'I skåp / Påfyllt (Eller max*)', selected: true },
  { id: SOLD_SINCE_LAST_RESTOCK, value: 'Sålda sen senaste påfyllning', selected: false },
]

export const dateRangeOptions = [
  {
    id: 0,
    value: 'Senaste 3 dagarna',
    date: new Date(Date.now() - 3 * ONE_DAY_IN_MS),
  },
  {
    id: 1,
    value: 'Senaste 7 dagarna',
    date: new Date(Date.now() - ONE_WEEK_IN_MS),
  },
  {
    id: 2,
    value: 'Senaste 14 dagarna',
    date: new Date(Date.now() - 2 * ONE_WEEK_IN_MS),
  },
  { id: 3, value: 'Från början', date: new Date('2000-01-01') },
]

export const numberOfSoldArticlesOptions = [
  {
    id: 0,
    value: 0,
    label: 'Minst 0 sålda',
  },
  {
    id: 1,
    value: 1,
    label: 'Minst 1 sålda',
  },
  {
    id: 2,
    value: 5,
    label: 'Minst 5 sålda',
  },
  {
    id: 3,
    value: 10,
    label: 'Minst 10 sålda',
  }
]

export function sortByTotalSold(items: Article[], direction: SortDirection = 'desc'): Article[] {
  return sortByNumber(items, item => item.totalSold ?? 0, direction)
}

interface IGroupAndFilterItemsProps {
  items: Item[]
  selectedSites: string[]
  selectedCategories: StoreActiveCategories[]
  filterDate: string
  filterSoldAmount: number
  stockStatusFilter?: string | string[] | null
  sortDirection?: SortDirection
}

export function groupAndFilterItems({
  items,
  selectedSites,
  selectedCategories,
  filterDate,
  filterSoldAmount,
  stockStatusFilter,
  sortDirection = 'desc'
}: IGroupAndFilterItemsProps): SelectedArticles {
  const groupedTotal: Record<string, Article[]> = {}
  const flatArticles: Article[] = []
  const startDate = filterDate ? new Date(filterDate) : null
  const categoriesSelected = selectedCategories.length > 0

  const selectedSitesSet = new Set(selectedSites)
  const selectedCategoryNamesSet = new Set(selectedCategories.map(cat => cat.categoryName))

  for (const item of items) {
    if (categoriesSelected && !selectedCategoryNamesSet.has(item.categoryName)) continue
    const {
      totalSold,
      totalInStock,
      totalRestocked,
      stockStatus,
      storeAmounts,
      hasMatchingStoreData
    } = getMatchingStoreData(item.storePickingData, selectedSitesSet, startDate)

    if (!hasMatchingStoreData || totalSold < filterSoldAmount) continue

    const shouldFilterByStock = stockStatusFilter && !(Array.isArray(stockStatusFilter) ? stockStatusFilter.includes(STOCK_STATUS_ALL) : stockStatusFilter === STOCK_STATUS_ALL)

    if (shouldFilterByStock) {
      if (Array.isArray(stockStatusFilter)) {
        if (!stockStatusFilter.includes(stockStatus)) continue
      } else {
        if (stockStatus !== stockStatusFilter) continue
      }
    }

    const article: Article = {
      ean: item.ean,
      name: item.name,
      totalSold,
      totalInStock,
      totalRestocked,
      stockStatus,
      storeAmounts,
    }

    addArticleToResult(article, groupedTotal, flatArticles, categoriesSelected, item.categoryName)
  }

  return formatResult(groupedTotal, flatArticles, categoriesSelected, sortDirection)
}

const statusPriority: Record<string, number> = {
  [STOCK_STATUS_OUT]: 2,
  [STOCK_STATUS_RUNNING_OUT]: 1,
  '': 0,
}

function getMatchingStoreData(
  storePickingData: StorePickingData[],
  selectedSitesSet: Set<string>,
  startDate: Date | null,
): {
  totalSold: number
  totalInStock: number
  totalRestocked: number
  stockStatus: string
  storeAmounts: StoreAmounts[]
  hasMatchingStoreData: boolean
} {
  const storeDataMap = new Map(storePickingData.map(data => [data.storeId, data]))
  const today = new Date()

  let totalSold = 0
  let totalInStock = 0
  let totalRestocked = 0
  const storeAmounts: StoreAmounts[] = []
  let hasMatchingStoreData = false
  let highestStatus = ''

  for (const storeId of selectedSitesSet) {
    const storeData = storeDataMap.get(storeId)
    if (!storeData) continue

    const restockedDate = new Date(storeData.restockedAt)
    const inDateRange = startDate ? restockedDate >= startDate && restockedDate <= today : true

    if (inDateRange) {
      hasMatchingStoreData = true
      totalSold += storeData.quantitySold
      totalInStock += storeData.quantityInStock
      totalRestocked += storeData.restockedQuantity
      const status = stockText({ inStockQuantity: storeData.quantityInStock, restockedQuantity: storeData.restockedQuantity, runningOutThreshold: storeData.runningOutThreshold }) || ''

      if (statusPriority[status] > statusPriority[highestStatus]) {
        highestStatus = status
      }

      storeAmounts.push({
        storeId: storeData.storeId,
        soldForStore: storeData.quantitySold,
        quantityInStockForStore: storeData.quantityInStock,
        restockedQuantityForStore: storeData.restockedQuantity,
        maxQuantityForStore: storeData.maxQuantity,
        status: status,
      })
    }
  }

  const stockStatus = highestStatus

  return { totalSold, totalInStock, totalRestocked, stockStatus, storeAmounts, hasMatchingStoreData }
}

function addArticleToResult(
  article: Article,
  groupedTotal: Record<string, Article[]>,
  flatArticles: Article[],
  categoriesSelected: boolean,
  categoryName: string
) {
  if (categoriesSelected) {
    if (!groupedTotal[categoryName]) {
      groupedTotal[categoryName] = []
    }
    groupedTotal[categoryName].push(article)
  } else {
    flatArticles.push(article)
  }
}

function formatResult(
  groupedTotal: Record<string, Article[]>,
  flatArticles: Article[],
  categoriesSelected: boolean,
  sortDirection: SortDirection = 'desc'
): Record<string, Article[]> | Article[] {
  return categoriesSelected
    ? Object.fromEntries(
      Object.entries(groupedTotal).map(([category, items]) => [
        category,
        sortByTotalSold(items, sortDirection),
      ])
    )
    : sortByTotalSold(flatArticles, sortDirection)
}

export function getSelectedItemsForPrint(allItems: SelectedArticles, selectedArticles: string[]): SelectedArticles {
  let itemsForPrint: SelectedArticles
  if(Array.isArray(allItems)) {
    itemsForPrint = allItems.filter((item) => selectedArticles.includes(item.ean))
  } else {
    itemsForPrint = Object.entries(allItems).reduce(
      (acc, [category, items]) => {
        const filtered = items.filter(item =>
          selectedArticles.includes(item.ean)
        )
        if (filtered.length > 0) {
          acc[category] = filtered
        }
        return acc
      },
      {} as Record<string, Article[]>
    )
  }

  return itemsForPrint
}

export function arraysEqualContent(arr1: string[], arr2: string[]): boolean {
  if (arr1.length !== arr2.length) return false
  const set1 = new Set(arr1)
  const set2 = new Set(arr2)
  if (set1.size !== set2.size) return false
  for (const val of set1) {
    if (!set2.has(val)) return false
  }
  return true
}

export function getTagTypeForStock(status: string): 'primary' | 'secondary' {
  return status === STOCK_STATUS_OUT ? 'primary' : 'secondary'
}

export function getSingleSiteMaxQuantity(
  storeAmounts: StoreAmounts[] | undefined,
  showSingleSiteMaxQuantity: boolean,
  articlesBySite: boolean
): number | null | undefined {
  return showSingleSiteMaxQuantity && !articlesBySite
    ? storeAmounts?.[0]?.maxQuantityForStore
    : undefined
}